import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react"
import "./presentation.less"
import { classNames, hasText } from "utils/helpers"
import Audio from "../components/media/audio"
import YouTube from "../components/media/youtube"
import PresentationImage from "../components/media/presentation-image"
import PresentationQuestion from "../components/media/presentation-question"
import { useMessages } from "../language/language"
import { usePlayState } from "play/hooks/use-play-state"

export default function Presentation() {
  const { screenIndex, screen } = usePlayState()

  return (
    <PresentationScreenView
      screenIndex={screenIndex}
      questionText={screen?.questionText}
      questionAudio={screen?.questionAudio}
      questionImage={screen?.questionImage}
      questionVideo={screen?.questionVideo}
      questionVideoStart={screen?.questionVideoStart || 0}
      questionVideoEnd={screen?.questionVideoEnd || 0}
      questionImageWidth={screen?.questionImageWidth || 0}
      questionImageHeight={screen?.questionImageHeight || 0}
    />
  )
}

type Props = {
  screenIndex: number
  questionText: string | undefined
  questionAudio: string | undefined
  questionImage: string | undefined
  questionVideo: string | undefined
  questionVideoStart: number
  questionVideoEnd: number
  questionImageWidth: number
  questionImageHeight: number
}

export function PresentationScreenView(props: Props) {
  const {
    questionImageWidth,
    questionImageHeight,
    questionText,
    questionImage,
    questionAudio,
    questionVideo,
    questionVideoStart,
    questionVideoEnd,
    screenIndex,
  } = props
  const msg = useMessages()
  const hasQuestion = useMemo(() => {
    if (!questionText) return false

    const div = document.createElement("div")
    div.innerHTML = questionText
    const len = div.innerText.length
    div.remove()
    return len > 0
  }, [questionText])

  const imageContainerRef = useRef<HTMLDivElement>(null)
  const [isZoomed, setIsZoomed] = useState(() => {
    const isZoomedByDefault =
      !hasQuestion && (hasText(questionImage) || hasText(questionVideo))
    return isZoomedByDefault
  })
  const [forceRedraw, setForceRedraw] = useState(0)

  function handleToggleZoom() {
    setIsZoomed((prev) => !prev)
  }

  useEffect(() => {
    const cantZoom = !hasText(questionImage) && !hasText(questionVideo)

    if (cantZoom) {
      setIsZoomed(false)
    }
  }, [questionText, questionImage, questionVideo, questionAudio])

  useLayoutEffect(() => {
    setForceRedraw(forceRedraw + 1)
    //console.log("redraw %d", forceRedraw);
  }, [imageContainerRef.current])

  const alt = msg.presentation_alt

  let media: JSX.Element | null
  let isVideo = false
  let isText = false

  if (hasText(questionAudio)) {
    isText = true
    media = (
      <Audio key={`${screenIndex}_${questionAudio}`} url={questionAudio!} />
    )
  } else if (hasText(questionVideo)) {
    isVideo = true
    media = (
      <YouTube
        url={questionVideo!}
        start={questionVideoStart}
        end={questionVideoEnd}
        toggleZoom={handleToggleZoom}
        isZoomed={isZoomed}
      />
    )
  } else if (hasText(questionImage)) {
    media = (
      <div
        className="presentation-image-container"
        ref={imageContainerRef}
        style={{
          width: isZoomed ? "100%" : `min(100%, ${questionImageWidth}px)`,
          height: isZoomed ? "100%" : `min(100%, ${questionImageHeight}px)`,
          maxWidth: "100%",
          maxHeight: "100%",
          overflow: "visible",
          // border: "2px dashed yellow",
          zIndex: 0,
        }}
      >
        <PresentationImage
          url={questionImage!}
          imageWidth={questionImageWidth}
          imageHeight={questionImageHeight}
          modifyHeight={true}
          toggleZoom={handleToggleZoom}
          isZoomed={isZoomed}
          zoomContainer={imageContainerRef}
          alt={alt}
        />
      </div>
    )
  } else {
    media = null
  }

  // if (media) {
  //   console.log('PRESENTATION HAS MEDIA')
  // } else {
  //   console.log('NO MEDIA')
  // }

  return (
    <div
      className={classNames("presentation-screen play-screen", {
        w: questionImageWidth >= questionImageHeight,
        h: questionImageWidth < questionImageHeight,
        zoomed: isZoomed && !!media,
        "text-only": !media,
        "media-only": !hasQuestion,
        video: isVideo,
      })}
    >
      <div className="presentation-top">{media}</div>
      {!isZoomed && (
        <div className="presentation-bottom">
          <PresentationQuestion question={questionText} />
        </div>
      )}
    </div>
  )
}
